(ns ycweb.app.website.search.instantsearch
  (:require
   [rum.core :as rum]
   [util.string :as str]
   [ycweb.app.website.common :as web-common]
   [ycweb.components.common :as cmn]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

(def search-listener (atom nil))

(defn focus-next-search-element
  [app-atom e]
  #?(:cljs
     (let [search-input (.querySelector js/document ".cljs-search-input")
           search-icon (.querySelector js/document ".cljs-search-submit-icon")
           search-close (.querySelector js/document ".cljs-close-icon")
           search-results (vec (.querySelectorAll js/document ".cljs-predictive-search__link"))
           first-tab (first search-results)
           last-tab (last search-results)
           active-tab (.-activeElement js/document)
           current-element-index
           (or
             (first
               (remove nil?
                 (map-indexed
                   (fn [idx element]
                     (when (= element active-tab)
                       idx))
                   search-results)))
             -1)
           pressed (cmn/curact app-atom :current-keys)]

       ;; Control Tab while Search is open.
       (cond
         (and
           (contains? pressed "Tab")
           (not
             (contains? pressed "Shift")))
         (cond
           (= active-tab search-icon)
           (do
             (.focus search-close)
             (.preventDefault e))

           (= active-tab search-close)
           (do
             (.focus search-input)
             (.preventDefault e)))

         (and
           (contains? pressed "Shift")
           (contains? pressed "Tab"))
         (cond
           (= active-tab search-close)
           (do
             (.focus search-icon)
             (.preventDefault e))

           (= active-tab search-input)
           (do
             (.focus search-close)
             (.preventDefault e))))

       ;; Close Modal on Esc press safely.
       (when (contains? pressed "Escape")
         (grid/dispatch-action app-atom :web.common/close-search-modal {}))

       ;; Navigate Search results
       (when search-results
         (cond
           (contains? pressed "ArrowUp")
           (when-not (= active-tab search-input)
             (cond
               (= active-tab first-tab)
               (.focus last-tab)

               :else
               (.focus (get search-results (dec current-element-index)))))

           (contains? pressed "ArrowDown")
           (cond
             (= active-tab last-tab)
             (.focus first-tab)

             (= active-tab search-input)
             (.focus first-tab)

             :else
             (.focus (get search-results (inc current-element-index))))

           (not (or (contains? pressed "Enter") (contains? pressed "Tab") (contains? pressed "Shift")))
           (.focus search-input)

           ;; Tab off the search results when focused on them.
           (and
             (contains? pressed "Tab")
             (.contains (.-classList active-tab) "cljs-predictive-search__link"))
           (do
             (.focus search-input)
             (.preventDefault e)))))))

(defn setup-search-keys [app-atom config]
  #?(:cljs
     (let [event-handler
           (fn [e]
             (focus-next-search-element app-atom e))]
       (reset! search-listener event-handler)
       (.addEventListener js/document "keydown"
         event-handler))))

(defmethod grid/action :web.common/close-search-modal [app-atom _]
  (grid/close-modal app-atom)
  #?(:cljs
     (.removeEventListener js/document "keydown"
       @search-listener)))

(defn highlight-searchstr [searchstr title]
  (let [found? (str/includes? (str/lower-case title) (str/lower-case searchstr))
        term-start (str/index-of (str/lower-case title) (str/lower-case searchstr))
        term-length (count searchstr)
        term-end (+ term-start term-length)]

    (cond
      found?
      [:span
       (str/subs title 0 term-start)
       [:b (str/subs title term-start term-end)]
       (str/subs title term-end)]

      :else
      title)))

(rum/defc instant-search-heading [title]
  [:li.predictive-search__item.predictive-search__item--heading
   [:div.predictive-search__heading
    [:div.predictive-search__text title]]])

(rum/defc instant-search-item [app-atom searchstr instant-search-uuid item]
  [:a.predictive-search__link.ui-corner-all.cljs-predictive-search__link
   {:on-click #(grid/dispatch-action app-atom :search/instant-search-stats {:item (:item/stats item) :ent/uuid instant-search-uuid})
    :href (:item/detail-url item)
    :tabIndex "-1"}
   (when (u/presence (:doc/public-id (:ent/image item)))
     [:div.predictive-search__image-wrapper
      (web-common/lazy-image app-atom
        (:doc/public-id (:ent/image item))
        {:class "predictive-search__image"}
        {:w 56
         :h 56
         :effect "sharpen:40"
         :f "auto"
         :c "lpad"
         :q "auto"})])
   [:div.predictive-search__text (highlight-searchstr searchstr (:ent/display-name item))]])

(rum/defc instant-search-help-item [app-atom searchstr instant-search-uuid article]
  [:a.predictive-search__link.ui-corner-all.cljs-predictive-search__link
   {:on-click
    #(grid/dispatch-action app-atom :search/instant-search-stats
       {:item (:item/stats article) :ent/uuid instant-search-uuid})
    :href (:item/detail-url article)
    :tabIndex "-1"}
   [:div.predictive-search__text
    (highlight-searchstr searchstr (:ent/display-name article))
    " - "
    [:em (:item/category article)]]])

(rum/defc instant-search-popup-body < rum/reactive [app-atom config]
  (let [screen (get-in @app-atom [:page :screen])
        process-atom (cmn/process-cursor app-atom)
        instant-search-atom (rum/cursor-in process-atom [:instant-search])
        instant-search (rum/react instant-search-atom)
        instant-search-uuid (:ent/uuid instant-search)

        {:instant-search/keys [orders products popular-searches categories help-articles searchstr]}
        instant-search

        show-results?
        (or
          (not-empty orders) (not-empty categories) (not-empty products)
          (not-empty popular-searches) (not-empty help-articles))

        help-article-limiter
        (cond
          (= screen :web.help)
          help-articles

          :else
          (take 7 help-articles))]

    [:<>
     [:form.search-bar
      (forms/input-raw "What are you looking for?"
        (rum/cursor-in instant-search-atom [:instant-search/searchstr])
        {:type :search
         :id "instant-search"
         :class "search-bar__input cljs-search-input"
         :no-border true
         :change-fn #(grid/dispatch-action app-atom :search/instant-search {})
         :auto-focus true})

      [:div.search-bar__submit-wrapper
       [:button.search-bar__submit-button.search-submit-button.cljs-search-submit-button
        (urlstate/href-on-click #(grid/dispatch-action app-atom :search/apply-instant-search {}))
        [:i
         {:class (u/bem :icon [:search :is-dark :block :default])}]
        [:span.u-hidden-from-view "Search"]]]

      [:nav.search-bar__close-wrapper
       [:a.search-bar__close-button.cljs-close-icon
        {:id :search-last-tab
         :href "#"
         :on-click
         #(do
            (.preventDefault %)
            (grid/dispatch-action app-atom :web.common/close-search-modal {}))}
        [:i {:class (u/bem :icon [:close :is-dark :block :default])}]
        [:span.u-hidden-from-view "Close search"]]]]

     (when show-results?
       [:ul.predictive-search
        (when (not-empty orders)
          (list
            (instant-search-heading "Track My Order")
            (map
              (fn [i]
                [:li.predictive-search__item.ui-menu-item
                 {:key (:item/detail-url i)}
                 (instant-search-item app-atom searchstr instant-search-uuid i)])
              orders)))

        (when (not-empty categories)
          [:<>
           (instant-search-heading "Categories & Brands")
           (map
             (fn [i]
               [:li.predictive-search__item.ui-menu-item
                {:key (:item/detail-url i)}
                (instant-search-item app-atom searchstr instant-search-uuid i)])
             categories)])

        (when (not-empty products)
          [:<>
           (instant-search-heading "Popular Products")
           (map
             (fn [i]
               [:li.predictive-search__item.ui-menu-item
                {:key (:item/detail-url i)}
                (instant-search-item app-atom searchstr instant-search-uuid i)])
             products)])

        (when (not-empty popular-searches)
          [:<>
           (instant-search-heading "Popular Searches")
           (map
             (fn [i]
               [:li.predictive-search__item.ui-menu-item
                {:key (:item/detail-url i)}
                (instant-search-item app-atom searchstr instant-search-uuid i)])
             popular-searches)])

        (when (not-empty help-articles)
          [:<>
           (instant-search-heading "Help Centre")
           (map
             (fn [article]
               [:li.predictive-search__item.ui-menu-item
                {:key (:item/detail-url article)}
                (instant-search-help-item app-atom searchstr instant-search-uuid article)])
             help-article-limiter)])])]))

(defmethod grid/modal-config :search/instant-search-popup [app-atom config _]
  {:body instant-search-popup-body
   :footer nil
   :requires-action false
   :modal-type :search})

(defmethod grid/action :search/instant-search-popup [app-atom config _]
  (swap! (cmn/process-cursor app-atom) dissoc :instant-search)
  (grid/open-modal app-atom :search/instant-search-popup {:background-type :search :config config})
  (setup-search-keys app-atom {}))

(defmethod grid/action :search/apply-instant-search [app-atom config]
  #?(:cljs
     (let [process-atom (cmn/process-cursor app-atom)
           instant-search (cmn/curact process-atom [:instant-search])
           instant-search-uuid (:ent/uuid instant-search)
           {:instant-search/keys [products]} instant-search
           searchstr (.-value (.getElementById js/document "instant-search"))
           stats (:item/stats (first products))
           redirect-url (:item/detail-url (first products))
           screen (get-in @app-atom [:page :screen])
           screen-base (get-in @app-atom [:page screen :base])]

       (cond
         (nil? searchstr)
         (toaster/open-toast app-atom :error "Enter a word to search for before clicking Search" {})

         (= screen-base "help")
         (do
           (urlstate/redirect app-atom "help" "search"
             {:q searchstr})
           (grid/close-modal app-atom))

         (= (count products) 1)
         (do
           (comms/ajax-get :web.search/stats
             {:stats
              (assoc
                (assoc stats
                  :stats/contentresults 1
                  :stats/firstresult redirect-url)
                :ent/uuid instant-search-uuid)})
           (urlstate/redirect app-atom redirect-url)
           (grid/close-modal app-atom))

         :else
         (do
           (urlstate/navigate app-atom nil (urlstate/url "/search.htm" {:searchstr searchstr}))
           (when (u/presence (:instant-search/page-meta-title instant-search))
             (set! (. js/document -title) (:instant-search/page-meta-title instant-search)))
           (grid/close-modal app-atom))))))

(defmethod grid/action :search/instant-search [app-atom _]
  (let [screen (get-in @app-atom [:page :screen])
        process-atom (cmn/process-cursor app-atom)
        {:instant-search/keys [searchstr]} (cmn/curact process-atom [:instant-search])
        screen-base (get-in @app-atom [:page screen :base])]
    (cond
      (> (count searchstr) 2)
      (comms/ajax-get :web.search/instant-search
        {:instant-search
         {:instant-search/searchstr searchstr
          :instant-search/screen screen-base}})

      :else
      (swap! (cmn/process-cursor app-atom) assoc
        :instant-search {:instant-search/searchstr searchstr}))))

(defmethod comms/receive :web.search/instant-search [app-atom e]
  (let [process-atom (cmn/process-cursor app-atom)
        {:instant-search/keys [searchstr]} (cmn/curact process-atom [:instant-search])
        {:instant-search/keys [orders products popular-searches categories help-articles page-meta-title]} (:instant-search e)]

    (swap! process-atom assoc
      :instant-search
      #:instant-search
       {:ent/uuid (:ent/uuid (:instant-search e))
        :page-meta-title page-meta-title
        :searchstr searchstr
        :orders orders
        :products products
        :popular-searches popular-searches
        :categories categories
        :help-articles help-articles})))

(defmethod grid/action :search/instant-search-stats [app-atom stats]
  (comms/ajax-get :web.search/stats
    {:stats (assoc (:item stats) :ent/uuid (:ent/uuid stats))}))

(defmethod comms/receive :web.search/stats [app-atom e])
